import { useLocation } from 'react-router-dom';
import { useState, useEffect, useMemo } from 'react';
import useSEO from '../../../../../../hooks/useSEO';
import {
  Box,
  Typography,
  LinearProgress,
  Card,
  Tooltip,
  IconButton,
  Skeleton,
  Chip,
} from '@mui/material';
import {
  FaUsers,
  FaHeart,
  FaHandHoldingUsd,
  FaWhatsapp,
  FaTwitter,
  FaFacebook,
  FaLink,
  FaQrcode,
} from 'react-icons/fa';
import COLORS from '../../../../../../constants/colors';
import DonateDialog from '../Donate/DonateDialog';
import baseURL from '../../../../user/config';
import DonationWidget from './DonationWidget';
import ImageGalleryDialog from './ImageGallery';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { QRCodeSVG } from 'qrcode.react';
import { Dialog, DialogContent, DialogTitle, Button as MuiButton } from '@mui/material';
import { Download, Close } from '@mui/icons-material';

// Main CampaignDetails component
// This component fetches and displays campaign details, including a donation widget
const CampaignDetails = () => {
  const { search } = useLocation();
  const query = new URLSearchParams(search);
  const id = query.get('id'); // Get id from query string
  const { state } = useLocation();

  const [campaign, setCampaign] = useState(state?.campaign || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  // NEW: State for additional images and their loading/error states
  const [otherImages, setOtherImages] = useState([]);
  const [imagesLoading, setImagesLoading] = useState(false);
  const [imagesError, setImagesError] = useState('');
  const [openGalleryDialog, setOpenGalleryDialog] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [shortUrl, setShortUrl] = useState('');
  const [openQRDialog, setOpenQRDialog] = useState(false);




  // ── Per-campaign SEO ─────────────────────────────────────────────────────
  const campaignStructuredData = useMemo(() => {
    if (!campaign) return null;
    const pageUrl = `https://yamba.africa/campaigns/${id}`;
    return {
      '@context': 'https://schema.org',
      '@type': 'DonateAction',
      name: campaign.name,
      description: typeof campaign.description === 'string'
        ? campaign.description.replace(/[#*_`[\]]/g, '').slice(0, 250)
        : '',
      url: pageUrl,
      image: campaign.image_url || 'https://yamba.africa/og-image.png',
      recipient: {
        '@type': 'Organization',
        name: 'Yamba Africa',
        url: 'https://yamba.africa',
      },
    };
  }, [campaign, id]);

  useSEO({
    title: campaign
      ? `${campaign.name} – Donate on Yamba Africa`
      : 'Campaign Details | Yamba Africa',
    description: campaign
      ? `Support "${campaign.name}" on Yamba Africa. ${(typeof campaign.description === 'string' ? campaign.description.replace(/[#*_`[\]]/g, '') : '').slice(0, 130)}...`
      : 'View campaign details and donate on Yamba Africa.',
    image: campaign?.image_url || 'https://yamba.africa/og-image.png',
    url: `https://yamba.africa/campaigns/${id}`,
    type: 'article',
    structuredData: campaignStructuredData,
  });
  // ─────────────────────────────────────────────────────────────────────────

  const formatAmount = (amount) => 'UGX ' + Math.floor(amount).toLocaleString();
  // Keep this for the *main* "Donate Now" button within the campaign details
  const [openMainDialog, setOpenMainDialog] = useState(false);

  // Sentence case helper
  const toSentenceCase = (str) => {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  // Effect to fetch main campaign details
  useEffect(() => {
    if (!campaign && id) {
      const fetchCampaign = async () => {
        try {
          setLoading(true);
          const res = await fetch(`${baseURL}/data/backend/campaigns/${id}`);
          if (!res.ok) throw new Error('Failed to fetch campaign data');
          const data = await res.json();
          setCampaign(data);
        } catch (err) {
          setError('Campaign not found or failed to load');
          console.error(err);
        } finally {
          setLoading(false);
        }
      };
      fetchCampaign();
    }
  }, [campaign, id]);

  // Effect to fetch additional images separately
  useEffect(() => {
    if (id) {
      const fetchImages = async () => {
        try {
          setImagesLoading(true);
          const res = await fetch(`${baseURL}/data/backend/campaigns/${id}/images`);
          if (!res.ok) throw new Error('Failed to fetch images');
          const data = await res.json();
          setOtherImages(data);
        } catch (err) {
          setImagesError('Failed to load additional images.');
          console.error(err);
        } finally {
          setImagesLoading(false);
        }
      };
      fetchImages();
    }
  }, [id]);

  // Fetch short link
  useEffect(() => {
    if (id) {
      const fetchShortLink = async () => {
        try {
          const res = await fetch(`${baseURL}/short-link/${id}`);
          if (res.ok) {
            const data = await res.json();
            setShortUrl(data.shortUrl);
          }
        } catch (err) {
          console.error('Failed to fetch short link:', err);
        }
      };
      fetchShortLink();
    }
  }, [id]);


  const progressPercentage = campaign && campaign.goal > 0
    ? ((campaign.raised / campaign.goal) * 100).toFixed(2)
    : "0.00";

  if (error) {
    return (
      <Typography variant="h6" color="error" textAlign="center" mt={4}>
        {error}
      </Typography>
    );
  }

  // Show skeletons when loading and no campaign data yet
  if (loading && !campaign) {
    return (
      <Box
        sx={{
          maxWidth: 1100,
          margin: '0 auto',
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 4,
          alignItems: 'flex-start',
          py: 6,
          px: { xs: 2, md: 4 },
        }}
      >
        {/* Left side skeleton */}
        <Box sx={{ width: { xs: '100%', md: '50%' } }}>
          <Skeleton variant="rectangular" height={250} sx={{ borderRadius: 4, mb: 3 }} />
          {/* Skeleton for the new image gallery */}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} variant="rectangular" width={80} height={80} sx={{ borderRadius: 2 }} />
            ))}
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 3 }}>
            {[...Array(3)].map((_, i) => (
              <Card
                key={i}
                sx={{ flex: 1, textAlign: 'center', p: 2, borderRadius: 4, boxShadow: 'none' }}
              >
                <Skeleton variant="circular" width={40} height={40} sx={{ mx: 'auto' }} />
                <Skeleton height={30} width="60%" sx={{ mx: 'auto', mt: 1, mb: 0.5 }} />
                <Skeleton height={20} width="80%" sx={{ mx: 'auto' }} />
              </Card>
            ))}
          </Box>
          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <Skeleton
              variant="rectangular"
              width={120}
              height={36}
              sx={{ borderRadius: 20 }}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Skeleton width={100} height={30} />
            {[...Array(5)].map((_, i) => (
              <Skeleton
                key={i}
                variant="circular"
                width={36}
                height={36}
                sx={{ mx: 0.5 }}
              />
            ))}
          </Box>
        </Box>

        {/* Right side skeleton */}
        <Box sx={{ width: { xs: '100%', md: '50%' } }}>
          <Skeleton height={48} width="70%" sx={{ mb: 3 }} />
          <Skeleton height={100} width="100%" sx={{ mb: 3 }} />
          <Skeleton height={30} width="80%" sx={{ mb: 1 }} />
          <Skeleton variant="rectangular" height={10} width="100%" sx={{ borderRadius: 5, mb: 3 }} />
          <Skeleton variant="rectangular" height={100} width="100%" sx={{ borderRadius: 4 }} />
        </Box>
      </Box>
    );
  }

  if (!campaign) {
    return (
      <Typography variant="h6" color="error" textAlign="center" mt={4}>
        Campaign not found
      </Typography>
    );
  }

  const progress = (campaign.raised / campaign.goal) * 100;
  const amountRemaining = campaign.goal - campaign.raised;

  const handleDonate = (amount, method, provider) => {
    console.log(
      `Donation details: ${amount} via ${method} (${provider}) for campaign ${campaign.name} (${campaign.id})`
    );
  };

  const currentUrl = window.location.href;
  const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(
    `Check out this campaign: ${campaign.name} - ${currentUrl}`
  )}`;
  const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
    `Check out this campaign: ${campaign.name}`
  )}&url=${encodeURIComponent(currentUrl)}`;
  const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
    currentUrl
  )}`;

  const handleCopyLink = () => {
    const linkToCopy = shortUrl || currentUrl;
    navigator.clipboard
      .writeText(linkToCopy)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {
        alert('Failed to copy link');
      });
  };

  const handleDownloadQR = () => {
    const svg = document.getElementById('campaign-qr-code');
    if (!svg) return;

    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      canvas.width = img.width * 2;
      canvas.height = img.height * 2;
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      const pngUrl = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.download = `campaign-qr-${id}.png`;
      link.href = pngUrl;
      link.click();
    };

    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  return (
    <Box
      sx={{
        maxWidth: 1100,
        margin: '0 auto',
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        gap: 4,
        alignItems: 'flex-start',
        py: 6,
        px: { xs: 2, md: 4 },
      }}
    >
      {/* Campaign Image & Donor Stats */}
      <Box sx={{ width: { xs: '100%', md: '50%' } }}>
        <Box
          component="img"
          src={campaign.image_url}
          alt={campaign.name}
          sx={{
            width: '100%',
            height: 300,
            borderRadius: 4,
            mb: 3,
            objectFit: 'cover',
          }}
        />

        {/* NEW: Additional Images Section */}
        <Box sx={{ mt: 2, mb: 3 }}>
          <Typography variant="subtitle1" fontWeight="bold" color={COLORS.primary} mb={1}>
            Image Gallery
          </Typography>
          {imagesLoading && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {[...Array(4)].map((_, i) => (
                <Skeleton key={i} variant="rectangular" width={80} height={80} sx={{ borderRadius: 2 }} />
              ))}
            </Box>
          )}
          {imagesError && <Typography color="error">{imagesError}</Typography>}
          {!imagesLoading && !imagesError && otherImages.length > 0 && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {otherImages.map((imgUrl, index) => (
                <Box
                  key={index}
                  component="img"
                  src={imgUrl}
                  alt={`Gallery image ${index + 1}`}
                  onClick={() => {
                    setOpenGalleryDialog(true);
                    setSelectedImageIndex(index);
                  }}
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: 2,
                    objectFit: 'cover',
                    border: `2px solid ${COLORS.border}`,
                    cursor: 'pointer', // Add cursor pointer to indicate it's clickable
                    '&:hover': {
                      borderColor: COLORS.primary,
                      transform: 'scale(1.05)',
                    },
                    transition: 'transform 0.2s ease-in-out',
                  }}
                />
              ))}
            </Box>
          )}
          {!imagesLoading && !imagesError && otherImages.length === 0 && (
            <Typography variant="body2" color={COLORS.text}>
              No additional images available.
            </Typography>
          )}
        </Box>

        {/* Campaign Stats */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 2,
            flexWrap: 'wrap',
            mb: 3,
          }}
        >
          <Card
            sx={{
              flex: 1,
              textAlign: 'center',
              p: 2,
              borderRadius: 4,
              boxShadow: 'none',
            }}
          >
            <FaUsers size={30} color={COLORS.primary} />
            <Typography variant="h6" fontWeight="bold" mt={1}>
              {campaign.donations}
            </Typography>
            <Typography variant="body2" color={COLORS.text}>
              Total Donors
            </Typography>
          </Card>

          <Card
            sx={{
              flex: 1,
              textAlign: 'center',
              p: 2,
              borderRadius: 4,
              boxShadow: 'none',
            }}
          >
            <FaHandHoldingUsd size={30} color={COLORS.accent} />
            <Typography
              variant="h6"
              fontWeight="bold"
              mt={1}
              sx={{ fontSize: '1rem' }}
            >
              {formatAmount(campaign.raised)}
            </Typography>
            <Typography variant="body2" color={COLORS.text}>
              Funds Raised
            </Typography>
          </Card>

          <Card
            sx={{
              flex: 1,
              textAlign: 'center',
              p: 2,
              borderRadius: 4,
              boxShadow: 'none',
            }}
          >
            <FaHeart size={30} color={COLORS.secondary} />
            <Typography
              variant="h6"
              color={COLORS.primary}
              sx={{ mb: 0.5 }}
            >
              {progressPercentage}%
            </Typography>
            <Typography variant="body2" color={COLORS.text}>
              Funded already
            </Typography>
          </Card>
        </Box>

        {/* Share Buttons */}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 3 }}>
          <Typography
            variant="subtitle1"
            mr={1}
            fontWeight="bold"
            color={COLORS.primary}
          >
            Share Campaign:
          </Typography>

          <Tooltip title="Share on WhatsApp">
            <IconButton
              aria-label="share on whatsapp"
              onClick={() => window.open(whatsappUrl, '_blank')}
              sx={{ color: '#25D366' }}
            >
              <FaWhatsapp size={24} />
            </IconButton>
          </Tooltip>

          <Tooltip title="Share on Twitter">
            <IconButton
              aria-label="share on twitter"
              onClick={() => window.open(twitterUrl, '_blank')}
              sx={{ color: '#1DA1F2' }}
            >
              <FaTwitter size={24} />
            </IconButton>
          </Tooltip>

          <Tooltip title="Share on Facebook">
            <IconButton
              aria-label="share on facebook"
              onClick={() => window.open(facebookUrl, '_blank')}
              sx={{ color: '#4267B2' }}
            >
              <FaFacebook size={24} />
            </IconButton>
          </Tooltip>

          <Tooltip title={copied ? 'Link Copied!' : 'Copy Link'}>
            <IconButton
              aria-label="copy link"
              onClick={handleCopyLink}
              sx={{ color: copied ? COLORS.secondary : COLORS.text }}
            >
              <FaLink size={24} />
            </IconButton>
          </Tooltip>

          <Tooltip title="Show QR Code">
            <IconButton
              aria-label="show qr code"
              onClick={() => setOpenQRDialog(true)}
              sx={{ color: COLORS.primary }}
            >
              <FaQrcode size={24} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Campaign Content & Donation Section */}
      <Box sx={{ width: { xs: '100%', md: '50%' }, textAlign: 'left' }}>
        <Typography
          variant="h6"
          fontWeight="700"
          color={COLORS.primary}
          mb={1}
          noWrap
          sx={{ textTransform: 'none', fontSize: '1.8rem' }}
        >
          {toSentenceCase(campaign.name)}
        </Typography>

        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 2 }}>
          {campaign.owner_type === 'Organization' ? (
            !!campaign.accepts_recurring ? (
              <Chip
                label="Org · Recurring"
                size="small"
                sx={{ fontSize: '0.72rem', fontWeight: 700, bgcolor: '#E8F5E9', color: '#2E7D32', border: '1px solid #A5D6A7' }}
              />
            ) : (
              <Chip
                label="Organization"
                size="small"
                sx={{ fontSize: '0.72rem', fontWeight: 700, bgcolor: '#E3F2FD', color: '#1565C0', border: '1px solid #90CAF9' }}
              />
            )
          ) : (
            !!campaign.accepts_recurring ? (
              <Chip
                label="Individual · Recurring"
                size="small"
                sx={{ fontSize: '0.72rem', fontWeight: 700, bgcolor: '#FFF8E1', color: '#E65100', border: '1px solid #FFCC80' }}
              />
            ) : (
              <Chip
                label="Individual"
                size="small"
                sx={{ fontSize: '0.72rem', fontWeight: 700, bgcolor: '#F3E5F5', color: '#6A1B9A', border: '1px solid #CE93D8' }}
              />
            )
          )}
        </Box>

        <Box
          sx={{
            color: COLORS.textSecondary,
            '& p:last-child': { mb: 0 },
            '& ul, & ol': { pl: 2 },
            mb: 2
          }}
        >
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeRaw]}
          >
            {campaign.description}
          </ReactMarkdown>
        </Box>

        {/* Progress Bar */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>
            {formatAmount(campaign.raised)} raised of {formatAmount(campaign.goal)} goal
          </Typography>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              height: 10,
              borderRadius: 5,
              backgroundColor: COLORS.border,
              '& .MuiLinearProgress-bar': { backgroundColor: COLORS.primary },
            }}
          />
        </Box>

        <Typography variant="h6" fontWeight="bold" color={COLORS.secondary}>
          Amount Remaining to Goal
        </Typography>
        <Typography variant="h6" fontWeight="bold" color={COLORS.primary} mb={3}>
          {formatAmount(amountRemaining)}
        </Typography>
      </Box>

      {/* --- Floating Donation Widget --- */}
      {campaign && <DonationWidget campaign={campaign} onDonate={handleDonate} />}

      {/* DonateDialog for the main "Donate Now" button */}
      <DonateDialog
        open={openMainDialog}
        onClose={() => setOpenMainDialog(false)}
        campaign={campaign}
        onDonate={handleDonate}
      />

      <ImageGalleryDialog
        open={openGalleryDialog}
        onClose={() => setOpenGalleryDialog(false)}
        images={otherImages}
        startIndex={selectedImageIndex}
      />

      {/* QR Code Dialog */}
      <Dialog
        open={openQRDialog}
        onClose={() => setOpenQRDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" fontWeight="bold" color={COLORS.primary}>
            Scan to Donate
          </Typography>
          <IconButton onClick={() => setOpenQRDialog(false)} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center', pb: 4 }}>
          <Box sx={{ p: 3, bgcolor: '#fff', borderRadius: 2, display: 'inline-block', mb: 2 }}>
            <QRCodeSVG
              id="campaign-qr-code"
              value={shortUrl || currentUrl}
              size={200}
              level="H"
              includeMargin
              fgColor={COLORS.primary}
            />
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Scan this QR code to visit the campaign page
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <MuiButton
              variant="outlined"
              startIcon={<Download />}
              onClick={handleDownloadQR}
              sx={{
                textTransform: 'none',
                borderColor: COLORS.primary,
                color: COLORS.primary,
              }}
            >
              Download
            </MuiButton>
            <MuiButton
              variant="contained"
              startIcon={<FaLink />}
              onClick={handleCopyLink}
              sx={{
                textTransform: 'none',
                bgcolor: COLORS.primary,
                '&:hover': { bgcolor: COLORS.secondary },
              }}
            >
              Copy Link
            </MuiButton>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default CampaignDetails;